import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Info, KeyRound, Mail, Pencil, ShieldCheck, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { formatLocalDate } from '@/lib/datetime';
import { cn } from '@/lib/utils';
import { messages } from '@/i18n';
import { useApi } from '@/api/apiContext';
import { ApiError } from '@/api/errors';
import {
  deleteUserMutationOptions,
  reactivateUserMutationOptions,
  resendInviteMutationOptions,
  resetSecondFactorsMutationOptions,
  suspendUserMutationOptions,
  updateUserMutationOptions,
  userOrganisationsQueryOptions,
  userQueryOptions,
  type UpdateUserRequest,
} from '@/api/endpoints/users';
import { DASHBOARD_ROLES, type DashboardRole } from '@/types/session';
import {
  initialsFor,
  patchCachedUserRow,
  removeCachedUserRow,
  STATUS_BADGE_CLASS,
  toDisplayUser,
} from './userDirectoryData';

// Backend endpoint `POST api/v1/users/{id}/second-factors/reset` is not live in production yet -
// flip this back on once it ships, and remove this switch. Exported so the tests gate on it too.
export const SECOND_FACTORS_RESET_ENABLED = false;

/** The editable state of a user, exactly as PUT /api/v1/users/{id} defines it. */
interface EditDraft {
  displayName: string;
  userType: DashboardRole;
  organisationKey: string;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs font-medium">{label}</p>
      <p className="mt-0.5 text-sm">{children}</p>
    </div>
  );
}

const INPUT_CLASS =
  'border-border bg-secondary/40 focus-visible:ring-ring h-9 rounded-md border px-3 text-sm outline-none focus-visible:ring-2';

export function UserDetailScreen() {
  const t = messages.access;
  const api = useApi();
  const queryClient = useQueryClient();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const userQuery = useQuery({ ...userQueryOptions(api, userId ?? ''), enabled: Boolean(userId) });
  const user = useMemo(
    () => (userQuery.data ? toDisplayUser(userQuery.data) : undefined),
    [userQuery.data],
  );
  const notFound =
    userQuery.isError && userQuery.error instanceof ApiError && userQuery.error.status === 404;

  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isConfirmingReset, setIsConfirmingReset] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  // Which organisations the *pending* role can be assigned to — the draft's
  // while editing, the saved row's otherwise, so this one query also names the
  // organisation shown in the read-only view. The server is the source of truth
  // for whether a role takes an organisation at all: it answers
  // organisationType: null with an empty items list for one that takes none.
  const organisationRole = draft?.userType ?? user?.role;
  const orgQuery = useQuery({
    ...userOrganisationsQueryOptions(api, organisationRole ?? 'Tollit'),
    enabled: organisationRole !== undefined,
  });
  const organisationOptions = orgQuery.data?.items ?? [];
  const needsOrganisation = orgQuery.data ? orgQuery.data.organisationType !== null : false;

  // Derived rather than synced through an effect: a key left over from the role
  // the admin just switched away from falls back to the first option in the
  // *current* list, so changing the role needs no separate draft reset.
  const selectedOrganisationKey =
    draft && organisationOptions.some((o) => o.key === draft.organisationKey)
      ? draft.organisationKey
      : (organisationOptions[0]?.key ?? '');

  function messageFor(error: unknown, fallback: string): string {
    return error instanceof ApiError ? (error.problem?.detail ?? error.message) : fallback;
  }

  const updateMutation = useMutation({
    ...updateUserMutationOptions(api),
    onSuccess: (updated) => {
      setActionError(null);
      patchCachedUserRow(queryClient, updated.id, () => updated);
      setDraft(null);
    },
    onError: (error) => setActionError(messageFor(error, t.editError)),
  });

  const suspendMutation = useMutation({
    ...suspendUserMutationOptions(api),
    onSuccess: (updated) => {
      setActionError(null);
      patchCachedUserRow(queryClient, updated.id, () => updated);
    },
    onError: (error) => setActionError(messageFor(error, t.suspendError)),
  });

  const reactivateMutation = useMutation({
    ...reactivateUserMutationOptions(api),
    onSuccess: (updated) => {
      setActionError(null);
      patchCachedUserRow(queryClient, updated.id, () => updated);
    },
    onError: (error) => setActionError(messageFor(error, t.reactivateError)),
  });

  // Resend leaves the row Invited, so there is nothing to patch — only the
  // fact that the email went out, announced in a transient live region.
  const resendInviteMutation = useMutation({
    ...resendInviteMutationOptions(api),
    onSuccess: () => {
      setActionError(null);
      setNotice(t.resendInviteSuccess);
      window.clearTimeout(noticeTimer.current);
      noticeTimer.current = window.setTimeout(() => setNotice(null), 2500);
    },
    onError: (error) => setActionError(messageFor(error, t.resendInviteError)),
  });

  // Like resend, a 204 with nothing to patch — only an announcement.
  const resetSecondFactorsMutation = useMutation({
    ...resetSecondFactorsMutationOptions(api),
    onSuccess: () => {
      setActionError(null);
      setIsConfirmingReset(false);
      setNotice(t.resetSecondFactorsSuccess);
      window.clearTimeout(noticeTimer.current);
      noticeTimer.current = window.setTimeout(() => setNotice(null), 2500);
    },
    onError: (error) => {
      setActionError(messageFor(error, t.resetSecondFactorsError));
      setIsConfirmingReset(false);
    },
  });

  const deleteMutation = useMutation({
    ...deleteUserMutationOptions(api),
    onSuccess: (id) => {
      removeCachedUserRow(queryClient, id);
      void navigate('..');
    },
    onError: (error) => {
      setActionError(messageFor(error, t.deleteError));
      setIsConfirmingDelete(false);
    },
  });

  if (userQuery.isLoading) {
    return (
      <section className="mx-auto max-w-2xl">
        <p className="text-muted-foreground text-sm">{t.loading}</p>
      </section>
    );
  }

  if (userQuery.isError && !notFound) {
    return (
      <section className="mx-auto max-w-2xl">
        <Link
          to=".."
          className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="size-4" aria-hidden />
          {t.backToUsers}
        </Link>
        <Alert variant="destructive">
          <Info aria-hidden />
          <AlertDescription>{t.loadError}</AlertDescription>
        </Alert>
      </section>
    );
  }

  if (!user) {
    return (
      <section className="mx-auto max-w-2xl">
        <Link
          to=".."
          className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="size-4" aria-hidden />
          {t.backToUsers}
        </Link>
        <Card>
          <CardContent className="pt-6">
            <h1 className="text-lg font-semibold">{t.userNotFoundTitle}</h1>
            <p className="text-muted-foreground mt-1 text-sm">{t.userNotFoundBody}</p>
          </CardContent>
        </Card>
      </section>
    );
  }

  // Narrowed by the early return above; aliased so the handlers below, which
  // TypeScript sees as escaping that narrowing, do not each need a non-null
  // assertion.
  const editing = user;

  function startEditing() {
    setActionError(null);
    setDraft({
      displayName: editing.name,
      userType: editing.role,
      organisationKey: editing.organisationKey ?? '',
    });
  }

  // PUT /api/v1/users/{id} takes the user's whole new editable state, not a
  // patch: role and organisation are one decision, and the server rejects an
  // organisation the submitted role isn't chosen from. Email and status stay
  // out of it — email is the account's Firebase identity, and status moves only
  // through the suspend/reactivate endpoints.
  function saveEdit() {
    if (!draft) return;
    const displayName = draft.displayName.trim();
    if (!displayName) return;
    if (needsOrganisation && !selectedOrganisationKey) return;

    const body: UpdateUserRequest = {
      displayName,
      userType: draft.userType,
      ...(needsOrganisation
        ? {
            organisationType: orgQuery.data!.organisationType!,
            organisationKey: selectedOrganisationKey,
          }
        : {}),
    };

    updateMutation.mutate({ id: editing.id, body });
  }

  const statusLabel =
    user.status === 'Active'
      ? t.statusActive
      : user.status === 'Invited'
        ? t.statusInvited
        : t.statusDeactivated;

  // Prefer the name the organisations endpoint gives for this key, falling back
  // to what the row alone resolves to while that query is in flight, or for a
  // key the role's current list no longer contains.
  const organisationLabel =
    organisationOptions.find((o) => o.key === user.organisationKey)?.name ??
    user.organisationName ??
    '—';

  const canSave =
    Boolean(draft?.displayName.trim()) &&
    !orgQuery.isLoading &&
    !orgQuery.isError &&
    !(needsOrganisation && !selectedOrganisationKey) &&
    !updateMutation.isPending;

  return (
    <section className="mx-auto max-w-2xl">
      <Link
        to=".."
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1.5 text-sm"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {t.backToUsers}
      </Link>

      <div className="mb-4 flex items-center gap-3">
        <span className="bg-primary/20 text-primary-foreground font-headline flex size-12 shrink-0 items-center justify-center rounded-full text-base font-semibold">
          {initialsFor(user.name)}
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{user.name}</h1>
          <p className="text-muted-foreground text-sm">{user.email}</p>
        </div>
      </div>

      {actionError ? (
        <Alert variant="destructive" className="mb-4">
          <Info aria-hidden />
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      ) : null}

      {/* Always rendered, so the live region exists before it has anything to
          announce — mounting it alongside the text can be missed. */}
      <p role="status" aria-live="polite" className="text-muted-foreground mb-4 text-sm empty:mb-0">
        {notice ?? ''}
      </p>

      <Card className="mb-6">
        <CardContent className="pt-6">
          {draft ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                {t.editNameLabel}
                <input
                  className={INPUT_CLASS}
                  value={draft.displayName}
                  onChange={(e) => setDraft((d) => (d ? { ...d, displayName: e.target.value } : d))}
                />
              </label>
              <div className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">{t.detailEmail}</span>
                <p className="flex h-9 items-center text-sm">{user.email}</p>
                <p className="text-muted-foreground text-xs">{t.editEmailLocked}</p>
              </div>
              <label className="flex flex-col gap-1 text-sm">
                {t.editUserTypeLabel}
                <select
                  className={INPUT_CLASS}
                  value={draft.userType}
                  onChange={(e) => {
                    const userType = e.target.value as DashboardRole;
                    // Clear the key along with the role: organisations come
                    // from a different table per role, so the outgoing role's
                    // key is never valid for the incoming one.
                    setDraft((d) => (d ? { ...d, userType, organisationKey: '' } : d));
                  }}
                >
                  {DASHBOARD_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {messages.roles[role]}
                    </option>
                  ))}
                </select>
              </label>
              {orgQuery.isLoading ? (
                <p className="text-muted-foreground self-end pb-2 text-sm">
                  {t.organisationLoading}
                </p>
              ) : needsOrganisation ? (
                <label className="flex flex-col gap-1 text-sm">
                  {t.editOrganisationLabel}
                  <select
                    className={INPUT_CLASS}
                    value={selectedOrganisationKey}
                    onChange={(e) =>
                      setDraft((d) => (d ? { ...d, organisationKey: e.target.value } : d))
                    }
                  >
                    {organisationOptions.map((org) => (
                      <option key={org.key} value={org.key}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <div className="flex flex-col gap-1 text-sm">
                  <span className="text-muted-foreground">{t.editOrganisationLabel}</span>
                  <p className="text-muted-foreground flex h-9 items-center text-xs">
                    {t.organisationNone}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t.detailEmail}>{user.email}</Field>
              <Field label={t.detailRole}>
                <Badge variant="accent">{messages.roles[user.role]}</Badge>
              </Field>
              <Field label={t.detailOrganisation}>{organisationLabel}</Field>
              <Field label={t.detailStatus}>
                <span
                  className={cn(
                    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium',
                    STATUS_BADGE_CLASS[user.status],
                  )}
                >
                  {statusLabel}
                </span>
              </Field>
              <Field label={t.detailDateAdded}>{formatLocalDate(user.dateAdded)}</Field>
            </div>
          )}

          {draft && orgQuery.isError ? (
            <Alert variant="destructive" className="mt-4">
              <Info aria-hidden />
              <AlertDescription>{t.organisationError}</AlertDescription>
            </Alert>
          ) : null}

          <div className="mt-6 flex justify-end gap-2">
            {draft ? (
              <>
                <Button variant="outline" onClick={() => setDraft(null)}>
                  {t.cancelEdit}
                </Button>
                <Button onClick={saveEdit} disabled={!canSave}>
                  {updateMutation.isPending ? t.savingChanges : t.saveChanges}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={startEditing}>
                <Pencil aria-hidden />
                {t.editUser}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className={isConfirmingDelete ? 'border-destructive/40' : undefined}>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
          {isConfirmingDelete ? (
            <>
              <p className="text-sm">{t.deleteUserConfirmBody}</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIsConfirmingDelete(false)}>
                  {t.deleteUserCancel}
                </Button>
                <Button
                  variant="destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(user.id)}
                >
                  {t.deleteUserConfirmAction}
                </Button>
              </div>
            </>
          ) : (
            <>
              {user.status === 'Deactivated' ? (
                <Button
                  variant="outline"
                  disabled={reactivateMutation.isPending}
                  onClick={() => reactivateMutation.mutate(user.id)}
                >
                  <ShieldCheck aria-hidden />
                  {t.reactivateUser}
                </Button>
              ) : user.status === 'Active' ? (
                <Button
                  variant="outline"
                  disabled={suspendMutation.isPending}
                  onClick={() => suspendMutation.mutate(user.id)}
                >
                  <ShieldCheck aria-hidden />
                  {t.suspendUser}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  disabled={resendInviteMutation.isPending}
                  onClick={() => resendInviteMutation.mutate(user.id)}
                >
                  <Mail aria-hidden />
                  {t.resendInvite}
                </Button>
              )}
              {/* An invited user has never signed in, so has nothing to reset. */}
              {SECOND_FACTORS_RESET_ENABLED && user.status !== 'Invited' ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setActionError(null);
                    setIsConfirmingReset(true);
                  }}
                >
                  <KeyRound aria-hidden />
                  {t.resetSecondFactors}
                </Button>
              ) : null}
              <Button
                variant="destructive"
                onClick={() => {
                  setActionError(null);
                  setIsConfirmingDelete(true);
                }}
              >
                <Trash2 aria-hidden />
                {t.deleteUser}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={isConfirmingReset}
        onOpenChange={(open) => {
          if (!resetSecondFactorsMutation.isPending) setIsConfirmingReset(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.resetSecondFactorsConfirmTitle}</DialogTitle>
            <DialogDescription>{t.resetSecondFactorsConfirmBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConfirmingReset(false)}
              disabled={resetSecondFactorsMutation.isPending}
            >
              {t.resetSecondFactorsCancel}
            </Button>
            <Button
              variant="destructive"
              disabled={resetSecondFactorsMutation.isPending}
              onClick={() => resetSecondFactorsMutation.mutate(user.id)}
            >
              {t.resetSecondFactorsConfirmAction}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
